import Typography from '@mui/material/Typography';
import React from 'react';

import { COLORS } from '../../../configs/colors';

export interface SlaStatusCellProps {
  value: unknown;
}

interface StatusDisplay {
  label: string;
  color: string;
}

// Theme-resolved neutral — meets WCAG AA contrast on the table background,
// unlike the CSS `gray` keyword which renders at ~3.95:1.
const NA: StatusDisplay = { label: 'N/A', color: 'text.secondary' };

/**
 * Extracts the outcome code from a service-list SLA summary string.
 * Accepts either the plain list-API form (`"YYYY-MM-DD HH:mm:ss+00 , breached"`)
 * or a JSON blob with `slaOutcome.outcomeTypeCode`.
 *
 * Splits on the *last* comma (rather than the literal `' , '`) so whitespace
 * variations from the BFF — `,breached`, ` ,breached`, `, breached` — all
 * resolve to the same outcome code.
 */
const extractOutcomeCode = (raw: string): string | undefined => {
  try {
    const parsed = JSON.parse(raw) as { slaOutcome?: { outcomeTypeCode?: string } };
    const code = parsed?.slaOutcome?.outcomeTypeCode;
    if (typeof code === 'string' && code) return code.toLowerCase();
  } catch {
    // Not JSON — fall through to delimited parsing
  }
  const lastComma = raw.lastIndexOf(',');
  if (lastComma === -1) return undefined;
  const tail = raw
    .slice(lastComma + 1)
    .trim()
    .toLowerCase();
  return tail || undefined;
};

const getStatusDisplay = (outcomeCode: string | undefined): StatusDisplay => {
  switch (outcomeCode) {
    case 'breached':
      return { label: 'Breached', color: COLORS.SLABreached };
    case 'reviewed':
      return { label: 'Breached - Reviewed', color: COLORS.SLABreached };
    case 'met':
      return { label: 'Met', color: COLORS.Blue };
    case 'mitigated':
      return { label: 'Met - Mitigated', color: COLORS.Blue };
    default:
      return NA;
  }
};

/** Plain-text SLA outcome indicator for the service list grid and exports. */
export const SlaStatusCell: React.FC<SlaStatusCellProps> = ({ value }) => {
  if (value == null || value === '') {
    return <Typography sx={{ fontSize: '11px', color: NA.color }}>{NA.label}</Typography>;
  }

  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const display = getStatusDisplay(extractOutcomeCode(raw));

  return (
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: display.color }}>
      {display.label}
    </Typography>
  );
};
